import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import SequencedMinigameCardLayoutGroup from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/SequencedMinigameCardLayoutGroup';
import SlottedMinigameCardLayoutGroup from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/SlottedMinigameCardLayoutGroup';
import { SizedObject } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';

function AlignElements(
  layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | HorizontalLayoutGroup,
  hAlignment: 'left' | 'center' | 'right',
) {
  const { data } = layoutGroup;

  const elementList = data.parent.list;

  const maxWidth = data.maxWidth ?? Number.MAX_SAFE_INTEGER;
  const rowHeight = 108;

  // Start at row 0.
  // Y position will be row * height of highest placed element in this row.
  // X position will just be origin + running total distance for this row.
  // Place an element at the next location, update the running total distance.
  // If the running total distance has gone over the max width, increment the current
  // row and try again with this element.

  let currentRow = 0;
  let currentRowIndex = 0;
  let currentElementIndex = 0;
  let runningTotalDist = 0;
  let tallestElementHeightInRow = 0;
  const rowHeights: number[] = [];
  const maxDimension = 64;

  // A matrix to store the calculated coordinate for each element's
  // row + column
  const elementMatrix: SizedObject[][] = Array.from(
    { length: maxDimension },
    () => new Array(maxDimension).fill(null),
  );
  const candidateElementPositions: Map<SizedObject, { x: number; y: number }> =
    new Map();

  const goToNextRow = () => {
    currentRow++;
    currentRowIndex = 0;
    runningTotalDist = 0;
    rowHeights.push(tallestElementHeightInRow);
    tallestElementHeightInRow = 0;
  };

  const satisfyElement = (element: SizedObject, x: number, y: number) => {
    elementMatrix[currentRow][currentRowIndex] = element;
    candidateElementPositions.set(element, { x, y });
    currentRowIndex++;
    currentElementIndex++;

    // Update the current tallest element height from this row.
    const elementHeight = isSizedElement(element)
      ? element.getElementSize().y
      : element.displayHeight;
    tallestElementHeightInRow = Math.max(
      elementHeight,
      tallestElementHeightInRow,
    );
  };

  const getElementSize = (element: SizedObject) => {
    return isSizedElement(element)
      ? element.getElementSize().x
      : element.displayWidth;
  };

  while (currentElementIndex < elementList.length) {
    const originForThisRow = data.origin.x;
    const currElement = elementList[currentElementIndex] as SizedObject;
    const y =
      rowHeights.reduce((accumulator, current) => accumulator + current, 0) +
      currentRow * data.elementDist; // Get the sum of the row heights and add vertical padding.

    const x =
      originForThisRow + runningTotalDist + getElementSize(currElement) / 2;

    if (!currElement || !currElement.visible) {
      // Step over this element.
      currentElementIndex++;
      continue;
    }

    const elementWidth = getElementSize(currElement);

    // Case for if we have an element that's wider than the max width.
    if (elementWidth >= maxWidth) {
      if (runningTotalDist === 0) {
        // This is the only element in its row, so we are happy.
        // Leave it and move on to the next row.
        satisfyElement(currElement, x, y);
      }

      // In the other case, where the element wasn't the
      // only element in its row, we just try again placing
      // it in the next row.

      goToNextRow();
      continue;
    }

    // Update running total dist.
    runningTotalDist += elementWidth + data.elementDist;

    if (runningTotalDist > maxWidth) {
      // Don't update current element index because we need to run
      // this again on the next row.
      goToNextRow();
      continue;
    }

    satisfyElement(currElement, x, y);
  }

  // We've built the matrix, now assign the positions to everything.
  for (var row = 0; row <= maxDimension; row++) {
    const elements = elementMatrix[row];
    if (!elements || elements.filter((e) => e !== null).length === 0) continue;

    // Calculate x offset for this row.
    const totalRowWidth = getTotalElementWidth(elements, data.elementDist);
    let xOffset;
    let yOffset;
    switch (hAlignment) {
      case 'left':
      default:
        xOffset = 0;
        yOffset = 0;
        break;
      case 'center':
        xOffset = -totalRowWidth / 2;
        yOffset = -(currentRow * rowHeight) / 2;
        break;
      case 'right':
        xOffset = -totalRowWidth;
        yOffset = 0;
        break;
    }

    elements.forEach((element: SizedObject) => {
      if (element !== null) {
        const candidatePos = candidateElementPositions.get(element);
        if (candidatePos) {
          layoutGroup.positionElement(
            element,
            candidatePos.x + xOffset,
            candidatePos.y + yOffset,
          );
        }
      }
    });

    layoutGroup.rowCount = currentRow + 1;
  }
}

// function AlignElementsVertical(
//   layoutGroup:
//     | SlottedMinigameCardLayoutGroup
//     | SequencedMinigameCardLayoutGroup
//     | HorizontalLayoutGroup,
// ) {
//   const { data } = layoutGroup;

//   const elementList = data.parent.list;

//   const maxWidth = data.maxWidth ?? Number.MAX_SAFE_INTEGER;
//   const colWidth = 108;

//   // Start at column 0.
//   // Y position will be column * height of highest placed element in this column.
//   // X position will just be origin + running total distance for this column.
//   // Place an element at the next location, update the running total distance.
//   // If the running total distance has gone over the max width, increment the current
//   // column and try again with this element.

//   let currentColumn = 0;
//   let currentColumnIndex = 0;
//   let currentElementIndex = 0;
//   let runningTotalDist = 0;
//   let widestElementWidthInColumn = 0;
//   const columnWidths: number[] = [];
//   const maxDimension = 64;

//   // A matrix to store the calculated coordinate for each element's
//   // row + column
//   const elementMatrix: SizedObject[][] = Array.from(
//     { length: maxDimension },
//     () => new Array(maxDimension).fill(null),
//   );
//   const candidateElementPositions: Map<SizedObject, { x: number; y: number }> =
//     new Map();

//   const goToNextColumn = () => {
//     currentColumn++;
//     currentColumnIndex = 0;
//     runningTotalDist = 0;
//     columnWidths.push(widestElementWidthInColumn);
//     widestElementWidthInColumn = 0;
//   };

//   const satisfyElement = (element: SizedObject, x: number, y: number) => {
//     elementMatrix[currentColumn][currentColumnIndex] = element;
//     candidateElementPositions.set(element, { x, y });
//     currentColumnIndex++;
//     currentElementIndex++;

//     // Update the current tallest element height from this column.
//     const elementWidth = isSizedElement(element)
//       ? element.getElementSize().x
//       : element.displayWidth;
//     widestElementWidthInColumn = Math.max(
//       elementWidth,
//       widestElementWidthInColumn,
//     );
//   };

//   const getElementSize = (element: SizedObject) => {
//     return isSizedElement(element)
//       ? element.getElementSize().x
//       : element.displayWidth;
//   };

//   while (currentElementIndex < elementList.length) {
//     const originForThisRow = data.origin.x;
//     const currElement = elementList[currentElementIndex] as SizedObject;
//     const y =
//       rowHeights.reduce((accumulator, current) => accumulator + current, 0) +
//       currentRow * data.elementDist; // Get the sum of the row heights and add vertical padding.

//     const x =
//       originForThisRow + runningTotalDist + getElementSize(currElement) / 2;

//     if (!currElement || !currElement.visible) {
//       // Step over this element.
//       currentElementIndex++;
//       continue;
//     }

//     const elementWidth = getElementSize(currElement);

//     // Case for if we have an element that's wider than the max width.
//     if (elementWidth >= maxWidth) {
//       if (runningTotalDist === 0) {
//         // This is the only element in its row, so we are happy.
//         // Leave it and move on to the next row.
//         satisfyElement(currElement, x, y);
//       }

//       // In the other case, where the element wasn't the
//       // only element in its row, we just try again placing
//       // it in the next row.

//       goToNextRow();
//       continue;
//     }

//     // Update running total dist.
//     runningTotalDist += elementWidth + data.elementDist;

//     if (runningTotalDist > maxWidth) {
//       // Don't update current element index because we need to run
//       // this again on the next row.
//       goToNextRow();
//       continue;
//     }

//     satisfyElement(currElement, x, y);
//   }

//   // We've built the matrix, now assign the positions to everything.
//   for (var row = 0; row <= maxDimension; row++) {
//     const elements = elementMatrix[row];
//     if (!elements || elements.filter((e) => e !== null).length === 0) continue;

//     // Calculate x offset for this row.
//     const totalRowWidth = getTotalElementWidth(elements, data.elementDist);
//     let xOffset;
//     let yOffset;
//     switch (hAlignment) {
//       case 'left':
//       default:
//         xOffset = 0;
//         yOffset = 0;
//         break;
//       case 'center':
//         xOffset = -totalRowWidth / 2;
//         yOffset = -(currentRow * colWidth) / 2;
//         break;
//       case 'right':
//         xOffset = -totalRowWidth;
//         yOffset = 0;
//         break;
//     }

//     elements.forEach((element: SizedObject) => {
//       if (element !== null) {
//         const candidatePos = candidateElementPositions.get(element);
//         if (candidatePos) {
//           layoutGroup.positionElement(
//             element,
//             candidatePos.x + xOffset,
//             candidatePos.y + yOffset,
//           );
//         }
//       }
//     });

//     layoutGroup.rowCount = currentRow + 1;
//   }
// }

export function AlignLayoutGroupCenter(
  layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | HorizontalLayoutGroup,
) {
  AlignElements(layoutGroup, 'center');
}

export function AlignLayoutGroupLeft(
  layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | HorizontalLayoutGroup,
) {
  AlignElements(layoutGroup, 'left');
}

export function AlignLayoutGroupRight(
  layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | HorizontalLayoutGroup,
) {
  AlignElements(layoutGroup, 'right');
}

export function getTotalElementWidthFromLayoutGroup(
  layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | HorizontalLayoutGroup,
) {
  const { data } = layoutGroup;
  const elementList = data.parent.list;
  return getTotalElementWidth(elementList);
}

export function getTotalElementWidth(
  elements: Phaser.GameObjects.GameObject[],
  padding?: number,
) {
  let totalWidth = 0;
  for (var i = 0; i < elements.length; i++) {
    const element = elements[i] as SizedObject;
    if (!element || !element.visible) continue;
    if (isSizedElement(element)) {
      totalWidth += element.getElementSize().x;
    } else {
      totalWidth += element.displayWidth;
    }
    if (padding && i !== 0) {
      totalWidth += padding;
    }
  }
  return totalWidth;
}

export interface SizedElement {
  getElementSize: () => Phaser.Math.Vector2;
}

// Type guard for Sized Element.
export function isSizedElement(obj: unknown): obj is SizedElement {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'getElementSize' in obj &&
    typeof (obj as SizedElement).getElementSize === 'function'
  );
}

// Type guard Sized Object
export function isSizedObject(obj: unknown): obj is SizedObject {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'getBounds' in obj &&
    typeof (obj as SizedObject).getBounds === 'function'
  );
}

export function fitToContent(
  target: Phaser.GameObjects.NineSlice,
  content: Phaser.GameObjects.Container | SizedObject,
  padding?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  },
) {
  padding = { left: 0, right: 0, top: 0, bottom: 0, ...padding };

  const backingBounds = target.getBounds();
  let minX = backingBounds.left;
  let minY = backingBounds.top;
  let maxX = backingBounds.right;
  let maxY = backingBounds.bottom;

  const calculateSizedObject = (sizedObject: SizedObject) => {
    minX = Math.min(sizedObject.getBounds().left, minX);
    minY = Math.min(sizedObject.getBounds().top, minY);
    maxX = Math.max(sizedObject.getBounds().right, maxX);
    maxY = Math.max(sizedObject.getBounds().bottom, maxY);
  };

  const recursiveCheck = (content: Phaser.GameObjects.GameObject) => {
    console.log('recursive checking', content);
    if (content instanceof Phaser.GameObjects.Container) {
      content.list.forEach((child: Phaser.GameObjects.GameObject) => {
        recursiveCheck(child);
      });
    } else if (isSizedObject(content)) {
      calculateSizedObject(content);
    }
  };

  recursiveCheck(content);

  if (
    minX < backingBounds.left ||
    maxX > backingBounds.right ||
    minY < backingBounds.top ||
    maxY > backingBounds.bottom
  ) {
    const newWidth = maxX - minX + (padding.right || 0) + (padding.left || 0);
    const newHeight = maxY - minY + (padding.bottom || 0) + (padding.top || 0);
    target.setPosition(minX - (padding.left || 0), minY - (padding.top || 0));
    target.setSize(newWidth / target.scaleX, newHeight / target.scaleY);
  }

  return { minX, minY, maxX, maxY };
}
